import { createContext, useContext, useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { isFlagOn } from '@/flags';
import { usePostHogIdentify } from '@/hooks/use-posthog-identify';
import { useSentryIdentify } from '@/hooks/use-sentry-identify';
import { type Brand, type Whoami } from '@/types/whoami';
import { domainMeta } from '@/utils/domain-meta';
import { buildStandardEventProperties } from '@/utils/standard-event-properties';

import { PostHogPropertiesProvider } from './posthog-context';

const ADMIN_ROLE = 'Admin';

type AuthContextType = {
  whoami?: Whoami | null;
  account: Brand | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  brandLogoUrl: string | null;
  brandSquareLogoUrl: string | null;
  isAuthenticated: boolean;
  hasSourceAccess: boolean;
  canPostToJobBoards: boolean;
  isLoading: boolean;
  isError: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export enum PERMISSION_MAPPING {
  ADMIN = 'admin',
  POST_TO_JOB_BOARDS = 'post_to_job_boards',
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: whoami, isLoading, isError } = useAuth();

  const account = useMemo(() => {
    if (!whoami) return null;

    return whoami.brands.filter(brand => brand.isAccount)[0];
  }, [whoami]);

  const [brandLogoUrl, brandSquareLogoUrl] = useMemo(() => {
    if (!whoami) return [null, null];

    const brand = whoami.brands.find(b => b.default);
    if (!brand) return [null, null];

    const getUrl = (path?: string) => {
      if (!path) return null;
      return new URL(path, domainMeta.monolithOrigin).href;
    };
    return [getUrl(brand.logo_url), getUrl(brand.square_logo_url)];
  }, [whoami]);

  usePostHogIdentify(whoami);
  useSentryIdentify(whoami);

  const standardEventProperties = useMemo(
    () => buildStandardEventProperties(whoami),
    [whoami],
  );

  return (
    <AuthContext.Provider
      value={{
        whoami,
        account,
        isAdmin: whoami?.role === ADMIN_ROLE,
        isSuperAdmin: !!whoami?.is_super_admin,
        brandLogoUrl,
        brandSquareLogoUrl,
        isAuthenticated: !!whoami,
        hasSourceAccess: isFlagOn(whoami, 'sourcing-dashboard-user-access'),
        canPostToJobBoards: !!(
          whoami?.perms.includes(PERMISSION_MAPPING.ADMIN) ||
          whoami?.perms.includes(PERMISSION_MAPPING.POST_TO_JOB_BOARDS)
        ),
        isLoading,
        isError,
      }}
    >
      <PostHogPropertiesProvider properties={standardEventProperties}>
        {children}
      </PostHogPropertiesProvider>
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context)
    throw new Error('useAuthContext must be used within <AuthProvider>');

  return context;
};

type AuthenticatedContextType = Omit<AuthContextType, 'whoami'> & {
  whoami: Whoami;
};

/**
 * A type-safe variant of useAuthContext that guarantees a non-null user.
 *
 * Use this hook in components that are rendered after authentication guards
 * (e.g., inside AppRoutes where isAuthenticated and hasSourceAccess checks
 * have already passed). This provides TypeScript with the guarantee that
 * `user` is always defined, eliminating the need for null checks.
 *
 * @throws Error if called before authentication is complete
 */
export const useAuthenticatedContext = (): AuthenticatedContextType => {
  const context = useAuthContext();

  if (!context.whoami) {
    throw new Error('useAuthenticatedContext requires an authenticated user');
  }

  return context as AuthenticatedContextType;
};
